import { css } from '@emotion/react';

import { formatDateTime, kaiPartialIdPresenter } from '@kaiizen/kai-presenters';
import { ApiScan, ScanType } from '@kaiizen/kai-pro-api';
import { useScan } from '@kaiizen/kai-pro-api-react-state';

import { LockIcon } from '@/Client/Icons';
import { isDateToday } from '@/Client/Lib';
import { useNavigateToChangeOrder, useNavigateToScan, useNavigateToScope } from '@/Client/State';

import { LAYOUT2, TYPOGRAPHY2 } from '@/Client/Styles';
import { Button } from '@/Client/Widgets';

const SCAN_ROW_STYLE = css`
  label: SCAN_ROW_STYLE;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
  padding-bottom: ${LAYOUT2.spacing2}px;
  border-bottom: 1px solid var(--border2);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const SCAN_ROW_INFO_STYLE = css`
  label: SCAN_ROW_INFO_STYLE;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const SCAN_ROW_HEADER_STYLE = css`
  label: SCAN_ROW_HEADER_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
`;

const SCAN_ROW_TYPE_STYLE = css`
  label: SCAN_ROW_TYPE_STYLE;
  ${TYPOGRAPHY2.medium}
`;

const SCAN_ROW_DATE_STYLE = css`
  label: SCAN_ROW_DATE_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
`;

const SCAN_TYPE_STRINGS: Record<ScanType, string> = {
  initial: 'Initial Walk',
  changeRequest: 'Change Order',
  progress: 'Progress / Close Out',
  final: 'Final Walk',
  progressRework: 'Punch List',
  progressCompleted: 'Close Out',
  audienceDoc: 'Audience Doc',
};

export const HistoricalScanRow = ({ scan: inputScan, latestScan, isMostRecentApprovedScan }: {
  scan: ApiScan;
  latestScan: ApiScan | null;
  isMostRecentApprovedScan: boolean;
}) => {
  const { scan } = useScan(inputScan); // keep scan fresh in case it gets refreshed
  const navigateToScan = useNavigateToScan();
  const navigateToChangeOrderScope = useNavigateToChangeOrder();
  const navigateToScope = useNavigateToScope();
  const scanType = scan.scanType || 'initial';
  const showScopeButton = scan.scanLifecycleStatus === 'approved'
    && (scanType === 'initial' || scanType === 'changeRequest');

  const showActiveScanButton = scan.id === latestScan?.id
    && scan.scanLifecycleStatus !== 'approved'
    && (scanType === 'initial' || scanType === 'changeRequest' || (scanType === 'progress' && isDateToday(scan.updatedAt)));

  const showChangeOrderButton = !showActiveScanButton
    && scan.scanLifecycleStatus === 'approved'
    && scanType === 'changeRequest';

  const scanDate = formatDateTime(scan.updatedAt);

  return (
    <div css={SCAN_ROW_STYLE}>
      <div css={SCAN_ROW_INFO_STYLE}>
        <div css={SCAN_ROW_HEADER_STYLE}>
          <div css={SCAN_ROW_TYPE_STYLE}>
            <span css={TYPOGRAPHY2.mediumBold}>{SCAN_TYPE_STRINGS[scanType]}</span> (Outdated)
          </div>
          <div css={SCAN_ROW_DATE_STYLE}>
            <span css={TYPOGRAPHY2.xSmallBold}>Approved</span>
            {' ' + scanDate}
          </div>
        </div>
      </div>
      {showChangeOrderButton && (
        <Button
          buttonStyle='white'
          onClick={() => navigateToChangeOrderScope({ scanId: scan.id })}
        >
          <LockIcon />
          View Change Order (CO-{kaiPartialIdPresenter(scan.id)})
        </Button>
      )}
      {showScopeButton && !isMostRecentApprovedScan && (
        <Button
          buttonStyle='white'
          onClick={() => navigateToScope({ scanId: scan.id })}
        >
          <LockIcon />
          View Scope (SOW-{kaiPartialIdPresenter(scan.id)})
        </Button>
      )}
      {scanType === 'progressCompleted' && (
        <Button
          buttonStyle='white'
          onClick={() => navigateToScan({ scanId: scan.id, closeout: true })}
        >
          <LockIcon />
          View Close Out (FIN-{kaiPartialIdPresenter(scan.id)})
        </Button>
      )}
    </div>
  );
};
